import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Stack,
  Typography,
  CircularProgress,
  Alert,
  Switch,
} from "@mui/material";
import { Formik } from "formik";
import * as Yup from "yup";
import { toast } from "sonner";
import { delftBlue } from "@/theme/colors";
import FormInput from "@/components/forms/FormInput";
import FormTextArea from "@/components/forms/FormTextArea";
import FormSelect from "@/components/forms/FormSelect";
import MultiSelectField from "@/components/forms/FormMultiSelect";
import {
  useCreatePrestartFormMutation,
  useGetActivitiesQuery,
  useGetCompaniesQuery,
} from "@/app/features/dashboard/dashboardApiSlice";
import { useSelector } from "react-redux";
import { selectUser } from "@/app/features/auth/authSlice";
import Roles from "@/constants/roles";
import FormBuilder, { emptyFormConfig } from "./components/FormBuilder";

const validationSchema = Yup.object().shape({
  name: Yup.string()
    .required("Form name is required")
    .min(2, "Form name must be at least 2 characters")
    .max(100, "Form name cannot exceed 100 characters"),
  description: Yup.string()
    .required("Description is required")
    .min(10, "Description must be at least 10 characters")
    .max(500, "Description cannot exceed 500 characters"),
  activityId: Yup.string().required("Activity is required"),
  companyIds: Yup.array(),
  isActive: Yup.boolean(),
});

const PrestartFormFormPage = () => {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const isSuperAdmin = user?.role === Roles.SUPER_ADMIN;

  const { data: activitiesResponse, isLoading: isLoadingActivities } = useGetActivitiesQuery({
    page: 1,
    limit: 10000,
  });
  const { data: companiesData } = useGetCompaniesQuery({ page: 1, limit: 10000 });
  const [createPrestartForm, { isLoading: isCreating }] = useCreatePrestartFormMutation();

  const [formConfig, setFormConfig] = useState(emptyFormConfig());

  const activities = activitiesResponse?.data?.activities || [];
  const activityOptions = activities.map((activity) => ({
    code: activity._id || activity.id,
    name: `${activity.name}${activity.code ? ` (${activity.code})` : ""}`,
  }));

  const companies = companiesData?.data?.companies || companiesData?.companies || [];
  const companyOptions = companies.map((company) => ({
    code: company._id?.toString() || company.id?.toString() || company._id || company.id,
    name: company.name || "Unknown Company",
  }));

  if (!isSuperAdmin) {
    return (
      <Box sx={{ padding: 3 }}>
        <Alert severity="error">Only Super Admin can create form templates.</Alert>
        <Button onClick={() => navigate("/forms")} sx={{ mt: 2 }}>
          Back to Forms
        </Button>
      </Box>
    );
  }

  const initialValues = {
    name: "",
    description: "",
    activityId: "",
    companyIds: [],
    isActive: true,
  };

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    try {
      const payload = {
        name: values.name.trim(),
        description: values.description.trim(),
        activityId: values.activityId,
        formConfig: {
          ...formConfig,
          totalPages: formConfig.pages?.length || 0,
          submitConfig: {
            ...(formConfig.submitConfig || {}),
            additionalParams: {
              ...((formConfig.submitConfig && formConfig.submitConfig.additionalParams) || {}),
              activityId: values.activityId,
            },
          },
        },
        isActive: values.isActive,
      };
      if (values.companyIds?.length) {
        payload.companyIds = values.companyIds;
      }
      const result = await createPrestartForm(payload).unwrap();
      const created = result?.data?.prestartForm;
      const formId = created?.id || created?._id;
      const addedAsVersion = Boolean(result?.data?.addedAsVersion);
      const templateVersion = result?.data?.templateVersion || created?.templateVersion;
      toast.success(
        addedAsVersion
          ? result?.message || `Saved as version ${templateVersion} of the existing form`
          : "Form template created",
      );
      navigate(`/forms/${formId}`, {
        state: { refresh: true, templateVersion },
      });
    } catch (error) {
      const message = error?.data?.message || "Failed to create form template";
      setStatus({ message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      component="main"
      sx={{
        flexGrow: 1,
        alignSelf: "stretch",
        minHeight: "100vh",
        paddingTop: { xs: 2, sm: 2.5, md: 3 },
        paddingLeft: { xs: 1.5, sm: 2, md: 3 },
        paddingRight: { xs: 1.5, sm: 2, md: 3 },
        paddingBottom: { xs: 2, sm: 2.5, md: 3 },
      }}
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, isSubmitting, status, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <Stack spacing={3}>
              <Box>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 22, sm: 24, md: 28 },
                    fontFamily: "Audiowide, sans-serif",
                    fontWeight: 400,
                    marginBottom: 1,
                  }}
                >
                  Add Form Template
                </Typography>
                <Typography
                  sx={{
                    color: delftBlue[500],
                    fontSize: { xs: 13, sm: 14, md: 16 },
                    fontFamily: "Montserrat, sans-serif",
                  }}
                >
                  Create a shared activity template. If this activity already has a form, it is saved as a new version. Leave companies empty to keep the version as a draft.
                </Typography>
              </Box>

              {status?.message && <Alert severity="error">{status.message}</Alert>}

              {isLoadingActivities ? (
                <CircularProgress />
              ) : (
                <Stack spacing={2}>
                  <FormInput name="name" label="Form Name" placeholder="e.g. Kayak Prestart" required />
                  <FormTextArea
                    name="description"
                    label="Description"
                    placeholder="Describe what this template is for"
                  />
                  <FormSelect
                    name="activityId"
                    label="Activity"
                    items={activityOptions}
                    onChange={(e) => {
                      const activityId = e.target.value;
                      setFormConfig((current) => ({
                        ...current,
                        submitConfig: {
                          ...(current.submitConfig || {}),
                          additionalParams: {
                            ...((current.submitConfig && current.submitConfig.additionalParams) || {}),
                            activityId,
                          },
                        },
                      }));
                    }}
                  />
                  <MultiSelectField
                    name="companyIds"
                    label="Companies (optional — publish later if empty)"
                    items={companyOptions}
                  />
                  <Box>
                    <Typography
                      sx={{
                        color: delftBlue[500],
                        fontFamily: "Audiowide, sans-serif",
                        fontSize: 18,
                        mb: 1.5,
                      }}
                    >
                      Form builder
                    </Typography>
                    <FormBuilder value={formConfig} onChange={setFormConfig} />
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Switch
                      checked={values.isActive}
                      onChange={(e) => setFieldValue("isActive", e.target.checked)}
                    />
                    <Typography sx={{ fontFamily: "Montserrat, sans-serif", color: delftBlue[500] }}>
                      Active
                    </Typography>
                  </Stack>
                </Stack>
              )}

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button variant="outlined" onClick={() => navigate("/forms")} disabled={isSubmitting || isCreating}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting || isCreating}
                  sx={{
                    backgroundColor: delftBlue[500],
                    "&:hover": { backgroundColor: delftBlue[600] },
                  }}
                >
                  {isSubmitting || isCreating ? <CircularProgress size={24} /> : "Create Template"}
                </Button>
              </Stack>
            </Stack>
          </form>
        )}
      </Formik>
    </Box>
  );
};

export default PrestartFormFormPage;
